import { detailTabPathMatches } from '../../utils/url';

/**
 * Filter items displayed in the horizontal tab bar at a given level.
 * @param {DetailTabItem[] | null | undefined} items
 * @returns {DetailTabItem[]}
 */
export const getUsedItems = (items) => {
  if (!items) return [];
  return items.filter((item) => item.used);
};

/**
 * Find a tab item by id at the current tree level (non-recursive).
 * @param {DetailTabItem[] | null | undefined} items
 * @param {string} id
 * @returns {DetailTabItem | null}
 */
export const findTabItemById = (items, id) => {
  if (!items) return null;
  return items.find((item) => item.id === id) ?? null;
};

/**
 * Find a tab item by following a path of ids from the root.
 * @param {DetailTabItem[]} tabs
 * @param {string[]} ids
 * @returns {DetailTabItem | null}
 */
export const findTabItemByIds = (tabs, ids) => {
  if (!ids?.length || !tabs?.length) return null;

  let currentItems = tabs;
  let item = null;

  for (let i = 0; i < ids.length; i += 1) {
    item = findTabItemById(currentItems, ids[i]);
    if (!item) return null;
    currentItems = item.items ?? [];
  }

  return item;
};

/**
 * Return the first navigable path in a tab subtree (depth-first on used items).
 * @param {DetailTabItem | null | undefined} item
 * @returns {string | null}
 */
export const getFirstNavigablePath = (item) => {
  if (!item) return null;
  if (item.path) return item.path;
  if (!item.items?.length) return null;

  const usedChildren = getUsedItems(item.items);
  for (let i = 0; i < usedChildren.length; i += 1) {
    const path = getFirstNavigablePath(usedChildren[i]);
    if (path) return path;
  }

  return null;
};

/**
 * @param {DetailTabItem[]} items
 * @param {string} currentPagePath
 * @param {string[]} ancestors
 * @returns {string[] | null}
 */
const findActiveTabIdsInItems = (items, currentPagePath, ancestors) => {
  const usedItems = getUsedItems(items);

  for (let i = 0; i < usedItems.length; i += 1) {
    const item = usedItems[i];
    const currentAncestors = [...ancestors, item.id];

    // Prefer deepest match: parents often share pathname with descendants.
    if (item.items?.length) {
      const found = findActiveTabIdsInItems(item.items, currentPagePath, currentAncestors);
      if (found) return found;
    }

    if (detailTabPathMatches(currentPagePath, item.path)) {
      return currentAncestors;
    }
  }

  return null;
};

/**
 * Find the active tab id path from the current page path.
 * @param {string} currentPagePath - Current location (pathname + search), not page.path
 * @param {DetailTabItem[]} tabs - Available tabs
 * @returns {string[]}
 */
export const findActiveTabIdsFromPath = (currentPagePath, tabs) => {
  if (!tabs?.length) return [];

  if (!currentPagePath) {
    const firstUsed = getUsedItems(tabs)[0];
    return firstUsed ? [firstUsed.id] : [];
  }

  const found = findActiveTabIdsInItems(tabs, currentPagePath, []);
  if (found) return found;

  const firstUsed = getUsedItems(tabs)[0];
  return firstUsed ? [firstUsed.id] : [];
};

/**
 * Build tab rows for display (one row per used level).
 * @param {DetailTabItem[]} tabs
 * @param {string[]} activeTabIds
 * @returns {DetailTabItem[][]}
 */
export const getTabRows = (tabs, activeTabIds) => {
  if (!tabs?.length) return [];

  const rows = [getUsedItems(tabs)];
  let currentItems = tabs;

  for (let level = 0; level < activeTabIds.length; level += 1) {
    const id = activeTabIds[level];
    const item = findTabItemById(currentItems, id);
    if (!item?.items?.length) break;

    const children = getUsedItems(item.items);
    if (children.length === 0) break;

    rows.push(children);
    currentItems = item.items;
  }

  return rows;
};

/**
 * Resolve the navigation path when a tab is clicked.
 * @param {DetailTabItem[]} tabs
 * @param {string[]} activeTabIds
 * @param {number} level
 * @param {string} clickedId
 * @returns {string | null}
 */
export const resolveTabClickPath = (tabs, activeTabIds, level, clickedId) => {
  const ids = [...activeTabIds.slice(0, level), clickedId];
  const item = findTabItemByIds(tabs, ids);
  if (!item) return null;

  return item.path ?? getFirstNavigablePath(item);
};

/**
 * Whether a page path matches a used tab in the tree.
 * @param {DetailTabItem[]} tabs
 * @param {string} currentPagePath
 * @returns {boolean}
 */
export const isPathInUsedTabs = (tabs, currentPagePath) => {
  if (!tabs?.length || !currentPagePath) return false;
  return findActiveTabIdsInItems(tabs, currentPagePath, []) !== null;
};

/**
 * First navigable path among root-level used tabs (depth-first).
 * @param {DetailTabItem[]} tabs
 * @returns {string | null}
 */
export const getFirstNavigablePathFromTabs = (tabs) => {
  const usedItems = getUsedItems(tabs);

  for (let i = 0; i < usedItems.length; i += 1) {
    const path = getFirstNavigablePath(usedItems[i]);
    if (path) return path;
  }

  return null;
};

/**
 * Resolve navigation target after tabs config changes.
 * @param {DetailTabItem[]} tabs
 * @param {Array<string | null | undefined>} fallbacks
 * @returns {string | null}
 */
export const resolveRealignmentPath = (tabs, fallbacks = []) => {
  const navigable = getFirstNavigablePathFromTabs(tabs);
  if (navigable) return navigable;
  return fallbacks.find(Boolean) ?? null;
};

/**
 * Resolve navigation path after closing a tab.
 * Prefers the nearest used sibling (right, then left); otherwise climbs to the parent.
 * Expects `tabs` with the closed tab already marked `used: false`.
 * @param {DetailTabItem[]} tabs
 * @param {string[]} closedIdPath - Id path of the closed tab (root → leaf)
 * @returns {string | null}
 */
export const resolvePathAfterCloseTab = (tabs, closedIdPath) => {
  if (!tabs?.length || !closedIdPath?.length) return null;

  const parentIds = closedIdPath.slice(0, -1);
  const closedId = closedIdPath[closedIdPath.length - 1];
  const siblings = parentIds.length === 0
    ? tabs
    : (findTabItemByIds(tabs, parentIds)?.items ?? []);

  const closedIndex = siblings.findIndex((item) => item.id === closedId);
  if (closedIndex === -1) return null;

  const maxDistance = Math.max(closedIndex, siblings.length - 1 - closedIndex);
  for (let distance = 1; distance <= maxDistance; distance += 1) {
    const rightIndex = closedIndex + distance;
    if (rightIndex < siblings.length) {
      const rightSibling = siblings[rightIndex];
      if (rightSibling.used) {
        const path = getFirstNavigablePath(rightSibling);
        if (path) return path;
      }
    }

    const leftIndex = closedIndex - distance;
    if (leftIndex >= 0) {
      const leftSibling = siblings[leftIndex];
      if (leftSibling.used) {
        const path = getFirstNavigablePath(leftSibling);
        if (path) return path;
      }
    }
  }

  if (parentIds.length === 0) return null;

  const parent = findTabItemByIds(tabs, parentIds);
  const parentPath = parent ? (parent.path ?? getFirstNavigablePath(parent)) : null;
  if (parentPath) return parentPath;

  return resolvePathAfterCloseTab(tabs, parentIds);
};
